import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SensorIcon from 'commons/src/components/sensors/SensorIcon';
import { roundSensorValue } from 'commons/src/components/sensors/SensorUnit';
import { sensorUnits } from 'commons/src/constants';
import calcTextSize, { getParentSize } from './commonFunctions';

type Props = {
    sensorValue: number | null | undefined;
    unit: string;
    sensor: string;
    id: string;
    numberOfTilesInWidth: number;
};

const LargeSensorValue = ({ sensor, sensorValue, unit, id, numberOfTilesInWidth }: Props): React.ReactElement => {
    const { t: txt } = useTranslation();
    const [sensorValueFontSize, setSensorValueFontSize] = useState('60px');
    const [sensorNameFontSize, setSensorNameFontSize] = useState('30px');
    const [currentNumberOfTiles, setCurrentNumberOfTiles] = useState(numberOfTilesInWidth);

    const calcText = (text: HTMLElement, updateFont: (font: string) => void): void => {
        const { parentHeight, textHeight, parentWidth } = getParentSize(text);
        const parentContainerWidth = parentWidth;
        let parentContainerHeight = parentHeight * 0.8;
        if (currentNumberOfTiles && numberOfTilesInWidth > currentNumberOfTiles) {
            parentContainerHeight *= currentNumberOfTiles / numberOfTilesInWidth;
        }
        const maxTextGrowth = parentContainerHeight / textHeight;
        calcTextSize(text, parentContainerWidth, updateFont, maxTextGrowth);
    };
    const calculateTextSizes = () => {
        const sensorValueElement = document.getElementById(id);
        const sensorNameElement = document.getElementById(`${id}-sensor-name`);
        if (sensorValueElement) {
            calcText(sensorValueElement, setSensorValueFontSize);
        }
        if (sensorNameElement) {
            calcText(sensorNameElement, setSensorNameFontSize);
        }
    };

    useEffect((): void => {
        calculateTextSizes();
        setCurrentNumberOfTiles(numberOfTilesInWidth);
    }, [numberOfTilesInWidth]);

    useEffect(() => {
        window.addEventListener('resize', calculateTextSizes);
        return (): void => window.removeEventListener('resize', calculateTextSizes);
    }, []);

    const sensorHasValue = sensorValue !== undefined && sensorValue !== null && !Number.isNaN(sensorValue);
    const highUnit = unit === 'c' || unit === 'f';
    const value = roundSensorValue(sensorValue, unit);
    const sensorString = `${txt(sensor).toUpperCase()}${!highUnit ? `(${sensorUnits[unit]})` : ''}`;
    return (
        <div className="sensor--large">
            <div className="sensor__grid sensor__grid--top">
                <div className="sensor__grid-item">
                    <SensorIcon sensor={sensor} />
                </div>
                <div id="sensorGridItem" className="sensor__grid-item sensor__grid-item--right">
                    <div id={id} className="sensor--large__value" style={{ fontSize: sensorValueFontSize }}>
                        {sensorHasValue ? value : txt('NotAvailableAbbreviation')}
                        {sensorHasValue && highUnit && <span>{sensorUnits[unit]}</span>}
                    </div>
                </div>
            </div>
            <div className="sensor__grid sensor__grid--bottom">
                <div className="sensor__grid-item" />
                <div className="sensor__grid-item sensor__grid-item--right sensor__grid-item--name">
                    <div id={`${id}-sensor-name`} style={{ fontSize: sensorNameFontSize }}>
                        {sensorString}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LargeSensorValue;
